import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Avatar,
  Collapse,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  FormGroup,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  User,
  Building2,
  Phone,
  Mail,
  CreditCard,
  Banknote,
  FileText,
  Package,
  Truck,
  Factory,
  ChevronDown,
  ChevronUp,
  Save,
  Hash,
  MapPin,

} from 'lucide-react';
import { updateUserProfile } from '../../../firebase/user';
import { LocationAutocomplete } from '../../../shared/components/AutoComplete/LocationAutocomplete';

// ─── helpers ──────────────────────────────────────────────────────────────────

const formatIban = (value) => {
  const cleaned = value.replace(/\s/g, '').toUpperCase();
  return cleaned.match(/.{1,4}/g)?.join(' ') ?? cleaned;
};
const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{10,}$/;

const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Delivery method options for courier
const DELIVERY_METHODS = [
  { value: 'bike',          label: 'Polkupyörä'   },
  { value: 'car',           label: 'Henkilöauto'  },
  { value: 'van',           label: 'Pakettiauto'  },
  { value: 'truck',         label: 'Kuorma-auto'  },
  { value: 'electric-car',  label: 'Sähköauto'    },
  { value: 'heatbox',       label: 'Lämpölaukku'  },
  { value: 'taxi-license',  label: 'Taksilupa'    },
];

// ─── AccordionSection ─────────────────────────────────────────────────────────

const AccordionSection = ({ icon: Icon, title, chips = [], children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Box>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          py: 1,
          px: 0,
          borderRadius: 1,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon size={18} />
          <Typography variant="subtitle2" fontWeight={600}>{title}</Typography>
        </Box>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </Box>

      {/* Chips always visible below header */}
      {!open && chips.length > 0 && (
        <Box sx={{ pl: 3.5, pb: open ? 0.5 : 1 }}>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {chips.map((chip, i) => (
              <Chip
                key={i}
                icon={chip.icon}
                label={chip.label}
                size="small"
                variant={chip.filled ? 'filled' : 'outlined'}
                color={chip.color || 'default'}
                sx={{
                  height: 24,
                  fontSize: '0.75rem',
                  '& .MuiChip-icon': { marginLeft: '6px' },
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      <Collapse in={open}>
        <Box sx={{ pl: 3.5, pt: 1, pb: 1.5 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── SectionLabel ─────────────────────────────────────────────────────────────

const SectionLabel = ({ children }) => (
  <Typography
    variant="caption"
    fontWeight={600}
    color="text.secondary"
    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5, display: 'block' }}
  >
    {children}
  </Typography>
);

// ─── ProfileAndBillingSection ─────────────────────────────────────────────────

export const ProfileAndBillingSection = ({ user, userData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const buildInitial = useCallback((ud) => ({
    firstName:          ud?.firstName || '',
    lastName:           ud?.lastName  || '',
    email:              ud?.email     || user?.email || '',
    phone:              ud?.phone     || '',
    mode:               ud?.mode      || 'sender',
    type:               ud?.type      || 'individual',
    yNumber:            ud?.yNumber      || ud?.billing?.yNumber      || '',
    companyName:        ud?.companyName  || ud?.billing?.companyName      ||'',
    paymentIban:        ud?.payment?.iban        || '',
    paymentYNumber:     ud?.payment?.yNumber     || ud?.yNumber     || '',
    paymentCompanyName: ud?.payment?.companyName || ud?.companyName || '',
    billingMethod:      ud?.billing?.billingMethod || 'invoice',
    billingYNumber:     ud?.billing?.yNumber       || ud?.yNumber     || '',
    billingCompanyName: ud?.billing?.companyName   || ud?.companyName || '',
    billingReference:   ud?.billing?.reference     || '',
    
    // Courier-specific
    courierArea:        ud?.location?.city     || '',
    deliveryMethods:    ud?.deliveryMethods || [],
  }), [user?.email]);

  const [saved, setSaved] = useState(() => buildInitial(userData));
  const [form, setForm]   = useState(() => buildInitial(userData));

  useEffect(() => {
    const initial = buildInitial(userData);
    setSaved(initial);
    setForm(initial);
  }, [userData, buildInitial]);

  const isDirty = !isEqual(form, saved);

  const set = (field) => (e) => {
    const value = field === 'paymentIban' ? formatIban(e.target.value) : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setError(null);
    setSuccess(null);
  };

  const setToggle = (field) => (_, v) => {
    if (!v) return;
    setForm((prev) => ({ ...prev, [field]: v }));
    setError(null);
    setSuccess(null);
  };

  const toggleDeliveryMethod = (value) => {
    setForm((prev) => {
      const methods = prev.deliveryMethods.includes(value)
        ? prev.deliveryMethods.filter((v) => v !== value)
        : [...prev.deliveryMethods, value];
      return { ...prev, deliveryMethods: methods };
    });
    setError(null);
    setSuccess(null);
  };

  // live flags
  const isCourier = form.mode === 'courier';
  const isCompany = form.type === 'company';
  const showNames = form.type === 'individual' || isCourier;
  const showInvoiceDetails = !isCourier && form.billingMethod === 'invoice';

  // ── save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    console.log('saving with form values:', form);
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      if (showNames) {
        if (!form.firstName.trim()) throw new Error('Etunimi vaaditaan');
      }
      if (!form.email.trim()) throw new Error('Sähköposti vaaditaan');
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) throw new Error('Virheellinen sähköpostiosoite');
      if (form.phone && !/^\+?[\d\s\-]{8,}$/.test(form.phone)) throw new Error('Virheellinen puhelinnumero');
      if (isCompany) {
        if (!form.yNumber.trim())     throw new Error('Y-tunnus vaaditaan');
        if (!form.companyName.trim()) throw new Error('Yrityksen nimi vaaditaan');
      }
      if (isCourier && form.paymentIban && !ibanRegex.test(form.paymentIban.replace(/\s/g, ''))) {
        throw new Error('Virheellinen IBAN');
      }

      const payload = {
        email: form.email.trim(),
        phone: form.phone.trim(),
        mode:  form.mode,
        type:  form.type,
        ...(showNames
          ? { firstName: form.firstName.trim(), lastName: form.lastName.trim() }
          : { name: form.firstName.trim() }),
        ...(isCompany && { yNumber: form.yNumber.trim(), companyName: form.companyName.trim() }),
        ...(isCourier
          ? {
              payment: {
                iban:        form.paymentIban.replace(/\s/g, ''),
                yNumber:     form.paymentYNumber.trim()     || form.yNumber.trim(),
                companyName: form.paymentCompanyName.trim() || form.companyName.trim(),
              },
              courierInfo: {
                area: form.courierArea.trim(),
              },
              deliveryMethods: form.deliveryMethods,
            }
          : {
              billing: {
                billingMethod:    form.billingMethod,
                billingFrequency: 7,
                yNumber:     form.billingYNumber.trim()     || form.yNumber.trim(),
                companyName: form.billingCompanyName.trim() || form.companyName.trim(),
                reference:   form.billingReference.trim(),
              },
            }),
      };

      await updateUserProfile(user.uid, payload);
      setSaved({ ...form });
      setSuccess('Tiedot päivitetty onnistuneesti');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Tallennus epäonnistui');
    } finally {
      setLoading(false);
    }
  };

  const handleDiscard = () => {
    setForm({ ...saved });
    setError(null);
    setSuccess(null);
  };

  // ── initials ───────────────────────────────────────────────────────────────
  const initials = showNames
    ? ((form.firstName?.[0] || '') + (form.lastName?.[0] || '')).toUpperCase() || 'U'
    : (form.firstName?.[0] || form.email?.[0] || 'U').toUpperCase();

  // ── chip builders ──────────────────────────────────────────────────────────

  const roleChips = [
    {
      icon: isCourier ? <Truck size={12} /> : <Package size={12} />,
      label: isCourier ? 'Kuriiri' : 'Lähettäjä',
      filled: true,
      color: isCourier ? 'success' : 'primary',
    },
    {
      icon: isCompany ? <Building2 size={12} /> : <User size={12} />,
      label: isCompany ? 'Yritys' : 'Yksityishenkilö',
      filled: false,
    },
  ];

  const identityChips = [
    showNames && form.firstName && {
      icon: <User size={12} />,
      label: `${form.firstName} ${form.lastName}`.trim(),
    },
    !showNames && form.firstName && { icon: <User size={12} />, label: form.firstName },
    form.email && { icon: <Mail size={12} />,  label: form.email },
    form.phone && { icon: <Phone size={12} />, label: form.phone },
  ].filter(Boolean);

  const companyChips = [
    form.yNumber     && { icon: <Hash size={12} />,      label: form.yNumber },
    form.companyName && { icon: <Building2 size={12} />, label: form.companyName },
  ].filter(Boolean);

  const paymentChips = [
    form.paymentIban        && { icon: <Banknote size={12} />,  label: form.paymentIban },
    form.paymentCompanyName && { icon: <Building2 size={12} />, label: form.paymentCompanyName },
  ].filter(Boolean);

  const billingChips = [
    {
      icon: form.billingMethod === 'invoice' ? <FileText size={12} /> : <CreditCard size={12} />,
      label: form.billingMethod === 'invoice' ? 'Lasku' : 'Kortti',
    },
    form.billingReference && { icon: <FileText size={12} />, label: form.billingReference },
    showInvoiceDetails && form.billingCompanyName && { icon: <Building2 size={12} />, label: form.billingCompanyName },
  ].filter(Boolean);

  // Courier service chips
  const courierServiceChips = [
    form.courierArea && { icon: <MapPin size={12} />, label: form.courierArea },
    ...form.deliveryMethods.map((v) => {
      const opt = DELIVERY_METHODS.find((o) => o.value === v);
      return opt ? { label: opt.label } : null;
    }).filter(Boolean),
  ].filter(Boolean);

  // ── field shorthand ────────────────────────────────────────────────────────
  const F = (label, key, opts = {}) => (
    <TextField
      label={label}
      fullWidth
      size="small"
      value={form[key]}
      onChange={set(key)}
      disabled={loading}
      {...opts}
    />
  );

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>

      {/* Alerts */}
      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── Avatar / name header ── */}
      <Box sx={{
        display: 'flex',
        alignItems: { xs: 'center', sm: 'flex-start' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        mb: 2.5,
      }}>
        <Avatar sx={{
          width: { xs: 52, sm: 64 },
          height: { xs: 52, sm: 64 },
          bgcolor: 'primary.main',
          fontSize: '1.5rem',
          flexShrink: 0,
        }}>
          {initials}
        </Avatar>
        <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
          <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={700}>
            {showNames ? `${form.firstName} ${form.lastName}`.trim() || '—' : form.firstName || '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary">{form.email}</Typography>
        </Box>
      </Box>

      {/* ══════════════════════════════════════════════════════════
          SECTION 1 — Role & Type
      ══════════════════════════════════════════════════════════ */}
      <AccordionSection icon={User} title="Rooli ja tyyppi" chips={roleChips} defaultOpen={false}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SectionLabel>Käyttötapa</SectionLabel>
            <ToggleButtonGroup
              value={form.mode} exclusive fullWidth size="small"
              onChange={setToggle('mode')}
              disabled={loading}
            >
              <ToggleButton value="sender"  sx={{ gap: 1 }}><Package size={15} /> Lähettäjä</ToggleButton>
              <ToggleButton value="courier" sx={{ gap: 1 }}><Truck   size={15} /> Kuriiri</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={12}>
            <SectionLabel>Asiakastyyppi</SectionLabel>
            <ToggleButtonGroup
              value={form.type} exclusive fullWidth size="small"
              onChange={setToggle('type')}
              disabled={loading}
            >
              <ToggleButton value="individual" sx={{ gap: 1 }}><User    size={15} /> Yksityishenkilö</ToggleButton>
              <ToggleButton value="company"    sx={{ gap: 1 }}><Factory size={15} /> Yritys</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
      </AccordionSection>

      {/* ══════════════════════════════════════════════════════════
          SECTION 2 — Identity
      ══════════════════════════════════════════════════════════ */}
      <AccordionSection icon={User} title="Asiakastiedot" chips={identityChips} defaultOpen>
        <Grid container spacing={2}>
          {showNames ? (
            <>
              <Grid item xs={12} sm={6}>{F('Etunimi',   'firstName', { required: true })}</Grid>
              <Grid item xs={12} sm={6}>{F('Sukunimi',  'lastName',  { required: true })}</Grid>
            </>
          ) : (
            <Grid item xs={12}>{F('Yhteyshenkilö', 'firstName', { helperText: 'Valinnainen' })}</Grid>
          )}
          <Grid item xs={12} sm={6}>{F('Sähköposti',   'email', { type: 'email', required: true })}</Grid>
          <Grid item xs={12} sm={6}>{F('Puhelinnumero', 'phone', { type: 'tel', placeholder: '+358 40 123 4567', helperText: 'Valinnainen' })}</Grid>
        </Grid>
      </AccordionSection>

      {/* ══════════════════════════════════════════════════════════
          SECTION 3 — Company (conditional)
      ══════════════════════════════════════════════════════════ */}
      {isCompany && (
        <AccordionSection
          icon={Building2}
          title="Yritystiedot"
          chips={companyChips}
          defaultOpen={!form.companyName}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>{F('Y-tunnus',      'yNumber',     { placeholder: '1234567-8', required: true })}</Grid>
            <Grid item xs={12} sm={7}>{F('Yrityksen nimi', 'companyName', { required: true })}</Grid>
          </Grid>
        </AccordionSection>
      )}

      {/* ══════════════════════════════════════════════════════════
          SECTION 4A — Courier service info (courier only)
      ══════════════════════════════════════════════════════════ */}
      {isCourier && (
        <AccordionSection
          icon={MapPin}
          title="Kuljetusalue ja kalusto"
          chips={courierServiceChips}
          defaultOpen={!form.courierArea && form.deliveryMethods.length === 0}
        >
          <Grid container spacing={2.5}>
            {/* Area */}
            <Grid item xs={12}>
              <SectionLabel>Missä haluat suorittaa kuljetuksia</SectionLabel>
              <LocationAutocomplete
                label="Kuljetusalue"
                placeholder="esim. Helsinki"
                value={form.courierArea}
                onChange={(val) => {
                  setForm((prev) => ({ ...prev, courierArea: val }));
                  setError(null);
                  setSuccess(null);
                }}
                onLocationSelect={async (locationData) => {
                  const city = locationData.city || locationData.address || '';
                  setForm((prev) => ({ ...prev, courierArea: city }));
                  // Also persist location to user profile immediately
                  if (user?.uid) {
                    try {
                      await updateUserProfile(user.uid, {
                        location: {
                          coordinates: {
                            lat:  locationData.latitude,
                            long: locationData.longitude,
                          },
                          city:    locationData.city    || '',
                          address: locationData.address || '',
                        },
                      });
                    } catch (err) {
                      console.error('Failed to save location:', err);
                    }
                  }
                }}
                showDetectLocation
                countryCode="fi"
              />
            </Grid>

            {/* Vehicles */}
            <Grid item xs={12}>
              <SectionLabel>Kuljetusvälineet</SectionLabel>
              <FormGroup>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  columnGap: 2,
                }}>
                  {DELIVERY_METHODS.map((opt) => (
                    <FormControlLabel
                      key={opt.value}
                      control={
                        <Checkbox
                          size="small"
                          checked={form.deliveryMethods.includes(opt.value)}
                          onChange={() => toggleDeliveryMethod(opt.value)}
                          disabled={loading}
                        />
                      }
                      label={<Typography variant="body2">{opt.label}</Typography>}
                      sx={{ mx: 0, py: 0.25 }}
                    />
                  ))}
                </Box>
              </FormGroup>
            </Grid>
          </Grid>
        </AccordionSection>
      )}

      {/* ══════════════════════════════════════════════════════════
          SECTION 4B — Payment (courier)
      ══════════════════════════════════════════════════════════ */}
      {isCourier && (
        <AccordionSection
          icon={Banknote}
          title="Maksutiedot"
          chips={paymentChips}
          defaultOpen={!form.paymentIban}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>{F('Y-tunnus',       'paymentYNumber',     { placeholder: form.yNumber || '1234567-8' })}</Grid>
            <Grid item xs={12} sm={6}>{F('Yrityksen nimi', 'paymentCompanyName', { placeholder: form.companyName || '' })}</Grid>
            <Grid item xs={12}>
              <TextField
                label="IBAN" fullWidth size="small"
                value={form.paymentIban}
                onChange={(e) => setForm((p) => ({ ...p, paymentIban: formatIban(e.target.value) }))}
                disabled={loading}
                placeholder="FI12 3456 7890 1234 56"
                required
                error={!!form.paymentIban && !ibanRegex.test(form.paymentIban.replace(/\s/g, ''))}
                helperText={
                  form.paymentIban && !ibanRegex.test(form.paymentIban.replace(/\s/g, ''))
                    ? 'Virheellinen IBAN'
                    : 'Tilinumero IBAN-muodossa'
                }
              />
            </Grid>
          </Grid>
        </AccordionSection>
      )}

      {/* ══════════════════════════════════════════════════════════
          SECTION 5 — Billing (sender only)
      ══════════════════════════════════════════════════════════ */}
      {!isCourier && (
        <AccordionSection
          icon={CreditCard}
          title="Laskutustiedot"
          chips={billingChips}
          defaultOpen={false}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" disabled={loading}>
                <InputLabel>Laskutustapa</InputLabel>
                <Select value={form.billingMethod} onChange={set('billingMethod')} label="Laskutustapa">
                  <MenuItem value="invoice">
                    <Stack direction="row" alignItems="center" spacing={1}><FileText size={16} /><span>Lasku</span></Stack>
                  </MenuItem>
                  <MenuItem value="card">
                    <Stack direction="row" alignItems="center" spacing={1}><CreditCard size={16} /><span>Kortti</span></Stack>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              {F('Viite / osasto', 'billingReference', { helperText: 'Valinnainen' })}
            </Grid>

            {showInvoiceDetails && (
              <>
                <Grid item xs={12} sm={5}>{F('Y-tunnus',       'billingYNumber',     { placeholder: form.yNumber || '1234567-8', required: true })}</Grid>
                <Grid item xs={12} sm={7}>{F('Yrityksen nimi', 'billingCompanyName', { placeholder: form.companyName || '',       required: true })}</Grid>
              </>
            )}
          </Grid>
        </AccordionSection>
      )}

      {/* ── Save bar — only visible when dirty ──────────────────────────────── */}
      <Collapse in={isDirty}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: 2,
          justifyContent: 'flex-end',
          position: { xs: 'sticky', sm: 'static' },
          bottom: 0,
          p: { xs: 2, sm: 0 },
          mt: 3,
          mx: { xs: -2, sm: 0 },
          bgcolor: { xs: 'background.paper', sm: 'transparent' },
          borderTop: { xs: '1px solid', sm: 'none' },
          borderColor: { xs: 'divider', sm: 'transparent' },
          zIndex: { xs: 10, sm: 'auto' },
        }}>
          <Button
            variant="outlined"
            onClick={handleDiscard}
            disabled={loading}
            fullWidth={isMobile}
          >
            Hylkää muutokset
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Save size={18} />}
            onClick={handleSave}
            disabled={loading}
            fullWidth={isMobile}
          >
            Tallenna muutokset
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
};